import * as THREE from "three";
import { DEFAULT_APPEARANCE, type CharacterAppearance } from "@ghostland/shared";
import { CharBillboard, headingToDir } from "./charBillboard.js";
import { asOverheadUI } from "./overheadLayer.js";
import { compositeAtlas } from "../sprites/c3dPipeline.js";

/**
 * NPC system. NPCs reuse the proven c3d billboard (8-direction + base + walk, pixel-locked
 * just like the player) — that IS the "NPC pipeline": every NPC is a c3d paperdoll
 * appearance rendered with CharBillboard, so it animates in all 8 directions with a base
 * pose for free. Each NPC's look is FIXED in the roster below (locked + saved with the NPC),
 * so the same characters appear the same way on every load and for every client.
 *
 * Adventurers wander; the bartender stands behind the bar and turns to face the player.
 * NPCs are client-side ambiance scoped to the tavern interior — no server/schema changes.
 */

/** A node in a scripted conversation: a line + optional player replies. An option with no
 *  `next` ends the conversation when chosen. A node with NO options but a `next` is a
 *  CONTINUE node: the ▼ caret shows and a click / E advances to `next` (multi-phase speech). */
export interface DialogNode {
  text: string;
  options?: { label: string; next?: DialogNode; action?: NpcAction; quest?: boolean; questId?: string }[];
  next?: DialogNode;
}

/** A side-effect a dialogue option can trigger (handled by the world controller). */
export type NpcAction = "teleportSpawn" | "getPickaxe" | "sellOres" | "openCasino" | "openPoker" | "openShop" | "getCutter" | "openStorage" | "exitKapon" | "openBlackmarket" | "openSalon" | "openSkinnerShop" | "learnLeatherSmith" | "learnBlackSmith" | "openFoodShop" | "clockToggle" | "buyBuilding" | "openSupplies" | "applyJob" | "openMarket" | "openSellShop" | "openFurniShop" | "collectRegister" | "acceptIntro" | "openTackleShop" | "openSmithShop" | "acceptQuest" | "submitQuest" | "imprintFish" | "openFishBuyer" | "openGhostLabShop";

export interface NpcDef {
  name: string;
  appearance: CharacterAppearance;
  /** Random one-liners said on talk (used when there's no scripted `dialog`). */
  lines: string[];
  /** A scripted, branching conversation (takes precedence over `lines`). */
  dialog?: DialogNode;
  /** Max health shown on the target frame (no combat yet — purely a WoW-style stat). */
  health?: number;
}

interface Npc extends NpcDef {
  bb: CharBillboard;
  label: THREE.Sprite;
  head: HTMLCanvasElement | null;
  kind: "wander" | "face";
  x: number; z: number;          // world units
  faceAngle: number;
  fixedFace: number;             // for "face" NPCs: the fixed heading they stand at
  lookTimer: number;             // seconds left turning to face the player (while/after talking)
  moving: boolean;
  tx: number; tz: number; pause: number; // wander state
  health: number;
  maxHealth: number;
  /** The floating pixel "!" quest marker (lazily built; visible while this NPC offers a quest). */
  qmark: THREE.Sprite | null;
}

const ADVENTURER_LINES = [
  "Heard there's treasure out in the dunes.",
  "Keep your blade close past the gate.",
  "I've walked a hundred leagues for a warm ale.",
  "The desert nights are colder than they say.",
  "Lost my map two towns back. Don't ask.",
  "They say this place is haunted. I say it's home.",
  "Buy you a drink? ...No? Suit yourself.",
];

// Garment colourway indices (C3D_GARMENT_COLORS): 0 white,1 black,2 blue,3 leather,4 tan,5 green,6 red,7 purple.
// Top styles (C3D_TOP): 1 tee,2 ghosttee,3 circle(recolour),4 tech(recolour),5 coin(pre),6 suit(pre),7 winston(pre).
// Pre-coloured tops MUST use topColor 0 (drawn as-is). Hair: 0 brown,1 black,2 white,3 gray,4 blonde,5 red,6 pink,7 purple,8 blue,9 green.
const A = (o: Partial<CharacterAppearance>): CharacterAppearance => ({ ...DEFAULT_APPEARANCE, ...o });

/** Locked roster — fixed appearances showing off the wardrobe (each stays the same). */
const ADVENTURER_ROSTER: NpcDef[] = [
  // Taisho — matching purple Tech set (top + techpants), "Nike-tech" look.
  { name: "Taisho", health: 110, lines: ADVENTURER_LINES, appearance: A({ face: 1, skinTone: 3, hair: 4, hairColor: 1, top: 4, topColor: 7, bottom: 3, bottomColor: 7, shoes: 1, shoesColor: 1 }) },
  // Carl — full black formal suit + black trousers.
  { name: "Carl", health: 95, lines: ADVENTURER_LINES, appearance: A({ face: 0, skinTone: 4, hair: 3, hairColor: 0, top: 6, topColor: 0, bottom: 1, bottomColor: 1, shoes: 1, shoesColor: 3 }) },
  // Frankie — circle tee (green) + workpants + cap.
  { name: "Frankie", health: 100, lines: ADVENTURER_LINES, appearance: A({ face: 0, skinTone: 2, hair: 2, hairColor: 5, top: 3, topColor: 5, bottom: 2, bottomColor: 0, shoes: 1, shoesColor: 0, hat: 1, hatColor: 6 }) },
  // Elizabeth — coin tee (GhostLand coin) + blue jeans.
  { name: "Elizabeth", health: 90, lines: ADVENTURER_LINES, appearance: A({ face: 1, skinTone: 1, hair: 1, hairColor: 4, top: 5, topColor: 0, bottom: 1, bottomColor: 2, shoes: 1, shoesColor: 3 }) },
];

/** The bartender — "Winston": white shirt with black buttons, black trousers, leather shoes. */
const WINSTON_INTRO = "Well, if you are trying to visit ghost land, you will need someone from there to come pick you up.";
const BARTENDER: NpcDef = {
  name: "Winston",
  health: 200,
  appearance: A({ face: 0, skinTone: 2, hair: 3, hairColor: 3, top: 7, topColor: 0, bottom: 1, bottomColor: 1, shoes: 1, shoesColor: 3 }),
  lines: [WINSTON_INTRO],
  dialog: {
    text: WINSTON_INTRO,
    options: [
      {
        label: "How do I do that?",
        next: {
          text: "If you know someone who's been on ghost land, send them a message, or you will have to wait here until someone there shows up.",
          options: [{ label: "Leave" }],
        },
      },
      { label: "Leave" },
    ],
  },
};

/** The Skinner — a grizzled trapper at Winston's who sells the skinning knife + teaches Leather Smith. */
const SKINNER_INTRO = "Name's Hside. You skin beasts? I deal in knives and the leather trade.";
const SKINNER: NpcDef = {
  name: "Hide", health: 150,
  appearance: A({ face: 0, skinTone: 4, hair: 1, hairColor: 4, top: 0, topColor: 0, bottom: 0, bottomColor: 1, shoes: 1, shoesColor: 4 }),
  lines: [SKINNER_INTRO],
  dialog: {
    text: SKINNER_INTRO,
    options: [
      { label: "Buy a skinning knife ($5,000)", action: "openSkinnerShop" },
      {
        label: "Teach me Leather Smith",
        next: {
          text: "Bring me 20 hog hides and the craft is yours. Hand 'em over now if you've got them.",
          options: [
            { label: "Learn it (20 hog hides)", action: "learnLeatherSmith" },
            { label: "Not yet" },
          ],
        },
      },
      { label: "Leave" },
    ],
  },
};

// --- Village shopkeepers (placeholder dialogue until the shop/bank/casino systems land) ---
const CLOTHIER: NpcDef = {
  name: "Vex", health: 100,
  appearance: A({ face: 0, skinTone: 3, hair: 4, hairColor: 7, top: 4, topColor: 7, bottom: 3, bottomColor: 7, shoes: 1, shoesColor: 1 }), // purple jumpsuit (tech set)
  lines: ["Best fits left in the wasteland."],
  dialog: { text: "Welcome to Threads — best fits left in the wasteland.", options: [
    { label: "Browse clothes", action: "openFoodShop" }, // live, stock-driven menu (reflects the back-room chest)
    { label: "Leave" },
  ] },
};
const FOOD_VENDOR: NpcDef = {
  name: "Sal", health: 100,
  appearance: A({ face: 0, skinTone: 1, hair: 2, hairColor: 0, top: 0, topColor: 0, bottom: 1, bottomColor: 1, shoes: 1, shoesColor: 0 }),
  lines: ["Order up! What'll it be?"],
  dialog: { text: "Welcome to Watabaga — hog burgers, fries, and cold drinks. Hungry?", options: [
    { label: "Show me the menu", action: "openFoodShop" },
    { label: "Leave" },
  ] },
};
const FURNI_CLERK: NpcDef = {
  name: "Oke", health: 100,
  appearance: A({ face: 1, skinTone: 3, hair: 3, hairColor: 3, top: 1, topColor: 4, bottom: 1, bottomColor: 3, shoes: 1, shoesColor: 3 }),
  lines: ["Every home needs a good bed. And somewhere to hide the valuables."],
  dialog: { text: "Welcome to Okka — beds, safeboxes, everything a home needs. Place them anywhere in a property you own.", options: [
    { label: "Browse furniture", action: "openFurniShop" },
    { label: "Leave" },
  ] },
};
const BANKER: NpcDef = {
  name: "Mr. Vault", health: 120,
  appearance: A({ face: 0, skinTone: 2, hair: 3, hairColor: 1, top: 6, topColor: 0, bottom: 1, bottomColor: 1, shoes: 1, shoesColor: 3 }), // black suit
  lines: ["Your money's safe in the vault."],
  dialog: { text: "Welcome to the bank. Your money's safe in the vault — and so is your gear.", options: [
    { label: "Access my vault", action: "openStorage" },
    { label: "Leave" },
  ] },
};
const CASINO_HOST: NpcDef = {
  name: "Roxy", health: 100,
  appearance: A({ face: 1, skinTone: 1, hair: 1, hairColor: 5, top: 6, topColor: 0, bottom: 1, bottomColor: 1, shoes: 1, shoesColor: 1 }), // black formal suit (dealer)
  lines: ["Feeling lucky?"],
  dialog: { text: "Feeling lucky? Grab a stool at a table and press E to sit — all bets in cash.", options: [
    { label: "Leave" },
  ] },
};
/** The poker dealer — stands behind the Texas Hold'em felt, deals the cards, seats you on request. */
const POKER_DEALER: NpcDef = {
  name: "Ace", health: 100,
  appearance: A({ face: 0, skinTone: 2, hair: 3, hairColor: 0, top: 6, topColor: 0, bottom: 1, bottomColor: 1, shoes: 1, shoesColor: 1 }), // black dealer suit
  lines: ["Texas Hold'em — buy in and play."],
  dialog: { text: "Texas Hold'em, table stakes. Take a stool and press E to sit, then buy in.", options: [
    { label: "Sit at the poker table", action: "openPoker" },
    { label: "Leave" },
  ] },
};

/** The miner — gives the pickaxe once, then buys ore. Stands by the mine area. */
const MINER: NpcDef = {
  name: "Dusty", health: 120,
  appearance: A({ face: 0, skinTone: 4, hair: 2, hairColor: 0, top: 1, topColor: 1, bottom: 2, bottomColor: 0, shoes: 1, shoesColor: 1, hat: 1, hatColor: 1 }),
  lines: ["Rocks won't break themselves."],
  dialog: { text: "Need a pickaxe, or here to sell ore?", options: [
    { label: "Get a pickaxe", action: "getPickaxe" },
    { label: "Sell my ore", action: "sellOres" },
    // (Smithing itself is taught by Ember at the BLACKSMITH shop on the residence row.)
    { label: "Leave" },
  ] },
};

/** The gardener — gives a grass-cutter once. With it, cut grass tufts around the city for cash. */
const GARDENER: NpcDef = {
  name: "Fern", health: 100,
  appearance: A({ face: 0, skinTone: 3, hair: 1, hairColor: 9, top: 1, topColor: 5, bottom: 1, bottomColor: 5, shoes: 1, shoesColor: 3, hat: 1, hatColor: 5 }),
  lines: ["Grass grows fast around here."],
  dialog: { text: "Grass grows fast around here — want a cutter to earn some cash trimming it?", options: [
    { label: "Get a grass-cutter", action: "getCutter" },
    { label: "Leave" },
  ] },
};

/** The salon stylist inside the Ghost Land (city) tavern — changes your hair for a fee. */
const SALON: NpcDef = {
  name: "Coco", health: 100,
  appearance: A({ face: 1, skinTone: 2, hair: 2, hairColor: 6, top: 3, topColor: 7, bottom: 1, bottomColor: 1, shoes: 1, shoesColor: 1 }), // pink bob + purple circle tee
  lines: ["Time for a new look?"],
  dialog: { text: "Welcome to the salon, hon. Fancy a fresh cut and colour?", options: [
    { label: "Change my hair — $500", action: "openSalon" },
    { label: "Maybe later" },
  ] },
};

/** The city's Winston — modern tech-tavern keeper. Sends you back to the spawn island. */
const CITY_WINSTON: NpcDef = {
  name: "Winston", health: 200,
  appearance: A({ face: 0, skinTone: 2, hair: 3, hairColor: 3, top: 4, topColor: 7, bottom: 3, bottomColor: 1, shoes: 1, shoesColor: 1 }), // tech jacket + techpants
  lines: ["What do you need?"],
  dialog: { text: "What do you need?", options: [
    { label: "Teleport to the spawn island", action: "teleportSpawn" },
    { label: "Nothing, thanks" },
  ] },
};

// Gate guards — they won't let you walk out of the walled village.
const GUARD_SEALED = {
  text: "Gate's sealed tight. Nobody walks out past the wall — not while I'm standing here.",
  options: [
    { label: "Why not?", next: { text: "Wasteland out there'll chew you up. You leave the way you came in — by pickup, not on foot.", options: [{ label: "Understood" }] } },
    { label: "Leave" },
  ],
};
const guardAppearance = (skin: number): CharacterAppearance =>
  A({ face: 0, skinTone: skin, hair: 3, hairColor: 1, top: 4, topColor: 1, bottom: 1, bottomColor: 1, shoes: 1, shoesColor: 1, hat: 1, hatColor: 1 }); // dark uniform + cap
/** The EAST gate guard — the only way OUT of the city. He opens the gate to Kapon North Way, but
 *  ONLY for the armed (the exit action itself re-checks the weapon). */
const GUARD_E: NpcDef = {
  name: "Bryce", health: 150, appearance: guardAppearance(4), lines: ["Stay sharp out there."],
  dialog: { text: "East gate leads out to Kapon North Way — wild country. I only open it for the armed. Heading out?", options: [
    { label: "Head out to Kapon", action: "exitKapon" },
    { label: "Not yet" },
  ] },
};
const GUARD_W: NpcDef = { name: "Hale", health: 150, appearance: guardAppearance(2), lines: ["Gate's sealed."], dialog: GUARD_SEALED };

/** The BLACK MARKET trader — a hooded figure in a dark cloak who deals weapons for cash. */
const BLACKMARKET: NpcDef = {
  name: "Shade", health: 130,
  // Hood (dark cap) + cape (backAcc) + dark top & pants; shadowed face. A shady silhouette.
  appearance: A({ face: 0, skinTone: 5, hair: 0, hairColor: 0, top: 6, topColor: 1, bottom: 1, bottomColor: 1, shoes: 1, shoesColor: 1, hat: 1, hatColor: 1, backAcc: 1, backAccColor: 1 }),
  lines: ["...you didn't see me here.", "Cash only. No questions."],
  dialog: { text: "Looking for something the shops won't sell? Steel, smoke... and seeds that grow into money out in the Kapon dirt. Cash only, no questions.", options: [
    { label: "Show me the goods", action: "openBlackmarket" },
    { label: "Leave" },
  ] },
};

/** The Old Timer — owner of OLD TIMER'S BAITS (the tackle shed behind Okka Furniture). Wears the
 *  full fisherman set: bucket hat (hat 3), vest (top 11), waders (bottom 4), boots (shoes 2).
 *  Sells the fishing rod (its first in-game source) + bait worms. */
const FISHER: NpcDef = {
  name: "Old Timer", health: 120,
  // Gray cropped hair under the bucket hat — the hat sits on top of the head, so the hairline shows.
  appearance: A({ face: 0, skinTone: 3, hair: 3, hairColor: 3, top: 11, topColor: 0, bottom: 4, bottomColor: 0, shoes: 2, shoesColor: 0, hat: 3, hatColor: 0 }),
  lines: [
    "The lake out in Kapon's got fat koi, if you've the patience.",
    "A bad day fishing beats a good day working.",
  ],
  dialog: { text: "Rods, line, and the fattest worms this side of the Kapon lake. After somethin'?", options: [
    { label: "Buy bait & tackle", action: "openTackleShop" },
    { label: "Imprint a fish", action: "imprintFish" },
    { label: "Leave" },
  ] },
};

/** Marla the FISH MERCHANT — parked outside OLD TIMER'S BAITS with her scales and coin box,
 *  buying whatever the anglers haul out of the water (the generic sell panel prices the fish). */
const FISHMONGER: NpcDef = {
  name: "Marla", health: 100,
  // wears the same fisher gear as the Old Timer: bucket hat (hat 3) + fisher vest (top 11)
  appearance: A({ face: 1, skinTone: 3, hair: 2, hairColor: 6, top: 11, topColor: 0, bottom: 2, bottomColor: 1, shoes: 1, shoesColor: 0, hat: 3, hatColor: 0 }),
  lines: [
    "Fresh off the line? Let's weigh it.",
    "The Old Timer sells the rods — I buy what they catch.",
  ],
  dialog: { text: "Got fish? I pay coin on the spot — koi fetch a pretty penny.", options: [
    { label: "Sell fish", action: "openFishBuyer" },
    { label: "Leave" },
  ] },
};

/** Sal — a hooded wanderer pacing the village main road; the FIRST quest giver ("First
 *  Introduction"). Gray hood (hat 2) + gray hooded parka (top 10) + leather pants (techpants
 *  recoloured leather-brown). His conversation is built dynamically by the world controller
 *  (it depends on the player's quest state), so no static `dialog` here. */
export const SAL: NpcDef = {
  name: "Sal", health: 140,
  appearance: A({ face: 0, skinTone: 2, hair: 0, hairColor: 0, top: 10, topColor: 0, bottom: 3, bottomColor: 3, shoes: 1, shoesColor: 1, hat: 2, hatColor: 0 }),
  lines: [
    "Good to see you settling in.",
    "The road tells you everything, if you walk it enough.",
    "Keep your bag light and your bank heavy.",
  ],
};

/** A crisp pixel "!" on a canvas → the floating quest marker sprite (small, white, pixelated). */
function makeQuestBang(): THREE.Sprite {
  const W = 10, H = 16; // tiny canvas ⇒ chunky pixels when scaled (deliberately pixelated)
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  // black outline first (1px halo), then the white bar + dot on top — reads at any distance.
  const bar = { x: 3, y: 1, w: 4, h: 9 }, dot = { x: 3, y: 12, w: 4, h: 3 };
  ctx.fillStyle = "#000";
  for (const r of [bar, dot]) ctx.fillRect(r.x - 1, r.y - 1, r.w + 2, r.h + 2);
  ctx.fillStyle = "#f4f4f4";
  for (const r of [bar, dot]) ctx.fillRect(r.x, r.y, r.w, r.h);
  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter; tex.minFilter = THREE.NearestFilter;
  const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false }));
  sp.scale.set(0.17, 0.27, 1);
  sp.renderOrder = 999;
  asOverheadUI(sp);
  return sp;
}

/** Draw a clean pixel-art KEYCAP (square key with a beveled cream face) + a glyph, at (x,y) size
 *  `s`. `letter` is the cap's glyph — drawn as crisp pixels for "T" (a placeholder, easily swapped
 *  for another key/icon later); any other letter falls back to a blocky monospace draw. */
export function drawKeyCap(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, letter = "T"): void {
  ctx.fillStyle = "#15151b"; ctx.fillRect(x, y, s, s);                       // dark border / base
  ctx.fillStyle = "#e9dab6"; ctx.fillRect(x + 2, y + 2, s - 4, s - 5);       // cream face
  ctx.fillStyle = "#fff5d8"; ctx.fillRect(x + 2, y + 2, s - 4, 1);           // top highlight
  ctx.fillStyle = "#b8a576"; ctx.fillRect(x + 2, y + s - 4, s - 4, 1);       // bottom inner shadow
  ctx.fillStyle = "#241f14";
  if (letter === "T") {                                                       // crisp pixel "T"
    const bw = s - 8, sw = Math.max(2, Math.round(s * 0.12));
    ctx.fillRect(x + 4, y + 5, bw, sw);                                       // top bar
    ctx.fillRect(Math.round(x + s / 2 - sw / 2), y + 5, sw, s - 10);          // stem
  } else {
    ctx.font = `bold ${Math.round(s * 0.6)}px monospace`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(letter, x + s / 2, y + s / 2 + 1);
  }
}

/** A floating label: a name + a pixel KEYCAP, shown above the SELECTED npc / player. The key tells
 *  you what T does (talk / inspect). NearestFilter keeps it crisp + pixel-styled. */
export function makeSelectLabel(name: string, key = "T", color = "#f0e4c2"): THREE.Sprite {
  const fs = 20, pad = 5, gap = 6, cap = fs + 2;
  const measure = document.createElement("canvas").getContext("2d")!;
  measure.font = `${fs}px monospace`;
  const nameW = Math.ceil(measure.measureText(name).width);
  const w = pad + nameW + gap + cap + pad, h = cap + pad * 2;
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.font = `${fs}px monospace`; ctx.textBaseline = "middle"; ctx.textAlign = "left";
  ctx.fillStyle = "#000"; ctx.fillText(name, pad + 1, h / 2 + 2);            // drop shadow
  ctx.fillStyle = color; ctx.fillText(name, pad, h / 2 + 1);
  drawKeyCap(ctx, pad + nameW + gap, (h - cap) / 2, cap, key);
  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter; tex.minFilter = THREE.NearestFilter;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false }));
  sprite.scale.set((w / h) * 0.44, 0.44, 1);
  // Draw last (on top of everything) — without this the transparent ground sprites in the mine /
  // outside THREADS can sort nearer than the label at some camera angles and paint over it.
  sprite.renderOrder = 1000;
  asOverheadUI(sprite); // crisp overlay layer — never softened by the world depth-of-field
  return sprite;
}
const makeNpcLabel = makeSelectLabel;

/** Crop the NPC's head from its front-idle c3d cell → a small pixel portrait for dialogue. */
async function npcHead(a: CharacterAppearance): Promise<HTMLCanvasElement | null> {
  const atlas = await compositeAtlas(a);
  if (!atlas) return null;
  const C = 144;
  const src = atlas.getContext("2d")!.getImageData(0, 0, C, C).data; // front-idle cell (row0,col0)
  let x0 = C, y0 = C, x1 = -1, y1 = -1;
  for (let y = 0; y < C; y++) for (let x = 0; x < C; x++) {
    if (src[(y * C + x) * 4 + 3] > 40) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  }
  if (x1 < 0) return null;
  const bw = x1 - x0, bh = y1 - y0;
  const size = Math.max(Math.round(bh * 0.48), Math.round(bw * 0.7));
  const cxp = (x0 + x1) / 2;
  const sx = Math.round(cxp - size / 2), sy = Math.max(0, y0 - 2);
  const out = document.createElement("canvas");
  out.width = size; out.height = size;
  const octx = out.getContext("2d")!;
  octx.imageSmoothingEnabled = false;
  octx.drawImage(atlas, sx, sy, size, size, 0, 0, size, size);
  return out;
}

/** Floating label height above the NPC (lowered so it sits near the upper body). */
const LABEL_Y = 1.32;
/** Quest "!" heights: it takes the NAME's spot by default, and hops ABOVE the name while the
 *  NPC is selected (the label appears in its place). */
const QMARK_Y = 1.36;
const QMARK_Y_SELECTED = 1.72;

export class NpcManager {
  private readonly npcs: Npc[] = [];
  private visible = false;
  /** Every NPC's appearance composite + head render, so a caller can wait until they're textured. */
  private readonly loads: Promise<unknown>[] = [];

  /** `spawns` lists who to place + how they behave; `area` (optional) bounds wanderers. */
  constructor(
    private readonly scene: THREE.Scene,
    spawns: { def: NpcDef; kind: "wander" | "face"; x: number; z: number; face?: number }[],
    private readonly area?: { x0: number; z0: number; x1: number; z1: number },
  ) {
    for (const s of spawns) this.add(s.def, s.kind, s.x, s.z, s.face ?? 0);
  }

  private add(def: NpcDef, kind: "wander" | "face", x: number, z: number, face: number): void {
    const bb = new CharBillboard();
    this.loads.push(bb.setAppearance(def.appearance));
    bb.group.position.set(x, 0, z);
    bb.group.visible = false;
    this.scene.add(bb.group);
    const label = makeNpcLabel(def.name);
    label.position.set(x, LABEL_Y, z);
    label.visible = false;
    this.scene.add(label);
    const npc: Npc = {
      ...def, bb, label, head: null, kind, x, z,
      faceAngle: face, fixedFace: face, lookTimer: 0, moving: false, tx: x, tz: z, pause: 0.5 + Math.random() * 2,
      health: def.health ?? 100, maxHealth: def.health ?? 100,
      qmark: null,
    };
    this.npcs.push(npc);
    this.loads.push(npcHead(def.appearance).then((h) => { npc.head = h; }));
  }

  /** Resolves once every NPC's character sheet has composited + applied — used to hold a zone
   *  transition's loading veil until the NPCs are textured (not white). */
  ready(): Promise<void> {
    return Promise.all(this.loads).then(() => undefined, () => undefined);
  }

  /** Mark `npc` as being talked to: it turns to face the player for the next 10s, then reverts
   *  to its normal behaviour (fixed heading or wander). */
  lookAtPlayer(npc: Npc): void {
    npc.lookTimer = 10;
  }

  /** Show/hide the floating pixel "!" over an NPC (by name) — this NPC has a quest to offer.
   *  The marker is a child of the billboard group, so it follows + inherits zone visibility. */
  setQuestMarker(name: string, on: boolean): void {
    const n = this.npcs.find((x) => x.name === name);
    if (!n) return;
    if (on && !n.qmark) {
      n.qmark = makeQuestBang();
      n.qmark.position.set(0, QMARK_Y, 0);
      n.bb.group.add(n.qmark);
    }
    if (n.qmark) n.qmark.visible = on;
  }

  /** Temporarily show this manager's NPCs for an off-screen render (window views peeking into a
   *  zone the local player isn't in). Returns the restore fn. Positions stay where the last active
   *  update left them — fine for a snapshot. */
  revealForSnapshot(): () => void {
    const undo: (() => void)[] = [];
    for (const n of this.npcs) {
      if (n.bb.group.visible) continue;
      n.bb.group.visible = true;
      undo.push(() => { n.bb.group.visible = false; });
    }
    return () => { for (const u of undo) u(); };
  }

  /** Per-frame: wander adventurers, hold "face" NPCs at their fixed heading, turn whoever is
   *  being talked to toward the player, animate, and show the name+[E] label only for the
   *  currently SELECTED npc (set via left-click). */
  update(dt: number, camYaw: number, player: { x: number; z: number }, inside: boolean, selected: Npc | null): void {
    if (inside !== this.visible) {
      this.visible = inside;
      if (!inside) for (const n of this.npcs) { n.bb.group.visible = false; n.label.visible = false; }
    }
    if (!inside) return;

    const SPEED = 1.25;
    for (const n of this.npcs) {
      n.bb.group.visible = true;
      if (n.lookTimer > 0) {
        n.lookTimer -= dt; // talking: face the player, hold still, until the 10s lapses
        n.faceAngle = Math.atan2(player.x - n.x, player.z - n.z);
        n.moving = false;
      } else if (n.kind === "face" || !this.area) {
        n.faceAngle = n.fixedFace; // stand at a fixed heading (don't track the player)
        n.moving = false;
      } else if (n.pause > 0) {
        n.pause -= dt; n.moving = false;
        if (n.pause <= 0) { n.tx = this.area.x0 + Math.random() * (this.area.x1 - this.area.x0); n.tz = this.area.z0 + Math.random() * (this.area.z1 - this.area.z0); }
      } else {
        const dx = n.tx - n.x, dz = n.tz - n.z, d = Math.hypot(dx, dz);
        if (d < 0.12) { n.moving = false; n.pause = 1 + Math.random() * 3; }
        else { const s = Math.min(d, SPEED * dt); n.x += (dx / d) * s; n.z += (dz / d) * s; n.faceAngle = Math.atan2(dx, dz); n.moving = true; }
      }
      n.bb.group.position.set(n.x, 0, n.z);
      n.bb.setMotion(headingToDir(n.faceAngle), n.moving);
      n.bb.tick(dt * 1000, camYaw);
      n.label.position.set(n.x, LABEL_Y, n.z);
      n.label.visible = n === selected; // name + [E] only for the selected NPC
      // The quest "!" gently bobs where the name sits — and hops above the name while selected.
      if (n.qmark?.visible) {
        const base = n === selected ? QMARK_Y_SELECTED : QMARK_Y;
        n.qmark.position.y = base + Math.sin(performance.now() / 400) * 0.05;
      }
    }
  }

  /** Raycast against the NPC billboards → the clicked NPC (nearest hit), or null. */
  pick(raycaster: THREE.Raycaster): Npc | null {
    if (!this.visible) return null;
    // Hits come back sorted near→far; only count one that lands on an OPAQUE body pixel of the
    // sprite's current frame (not the transparent quad corners), so picking is pixel-accurate.
    const hits = raycaster.intersectObjects(this.npcs.map((n) => n.bb.group), true);
    for (const h of hits) {
      if (!h.uv) continue;
      const npc = this.npcs.find((n) => n.bb.group === h.object.parent);
      if (npc && npc.bb.hitTestUV(h.uv)) return npc;
    }
    return null;
  }

  /** NPCs within `range` world units of (x,z), nearest first — for Tab target cycling. */
  inRange(x: number, z: number, range: number): Npc[] {
    return this.npcs
      .map((n) => ({ n, d: (n.x - x) ** 2 + (n.z - z) ** 2 }))
      .filter((e) => e.d <= range * range)
      .sort((a, b) => a.d - b.d)
      .map((e) => e.n);
  }

  /** A random line for an NPC (single-line NPCs always say their one line). */
  static line(n: Npc): string { return n.lines[Math.floor(Math.random() * n.lines.length)]; }

  dispose(): void {
    for (const n of this.npcs) { this.scene.remove(n.bb.group); n.bb.dispose(); this.scene.remove(n.label); }
    this.npcs.length = 0;
  }
}

type Spawn = { def: NpcDef; kind: "wander" | "face"; x: number; z: number; face?: number };

/** The tavern's NPCs: 4 wandering adventurers (locked roster) + Winston behind the bar. */
export function tavernNpcs(
  scene: THREE.Scene,
  area: { x0: number; z0: number; x1: number; z1: number },
  barSpot: { x: number; z: number },
): NpcManager {
  const spawns: Spawn[] = ADVENTURER_ROSTER.slice(0, 4).map((def, i) => {
    const t = (i + 0.5) / 4;
    return { def, kind: "wander", x: area.x0 + t * (area.x1 - area.x0), z: area.z0 + ((i % 2) ? 0.65 : 0.3) * (area.z1 - area.z0) };
  });
  // Bartender stands behind the bar, facing out into the room (toward the area centre).
  spawns.push({ def: BARTENDER, kind: "face", x: barSpot.x, z: barSpot.z, face: faceTo(barSpot, area) });
  // The Skinner stands by the near-left corner of the tavern, facing the room.
  const skinSpot = { x: area.x0 + 0.18 * (area.x1 - area.x0), z: area.z1 - 0.2 * (area.z1 - area.z0) };
  spawns.push({ def: SKINNER, kind: "face", x: skinSpot.x, z: skinSpot.z, face: faceTo(skinSpot, area) });
  return new NpcManager(scene, spawns, area);
}

/** A fixed heading pointing from a stand spot toward the centre of a room area. */
function faceTo(spot: { x: number; z: number }, area: { x0: number; z0: number; x1: number; z1: number }): number {
  return Math.atan2((area.x0 + area.x1) / 2 - spot.x, (area.z0 + area.z1) / 2 - spot.z);
}

/** The village shopkeepers: clothier (purple jumpsuit), banker, casino host — all static. */
/** The marketplace broker — stands outside the warehouse; opens the player marketplace. */
const MARKETEER: NpcDef = {
  name: "Mara", health: 110,
  appearance: A({ face: 1, skinTone: 2, hair: 2, hairColor: 7, top: 4, topColor: 7, bottom: 3, bottomColor: 7, shoes: 1, shoesColor: 1, hat: 1, hatColor: 7 }),
  lines: ["Buy low, sell high."],
  dialog: { text: "Welcome to the marketplace. Browse what folk are selling, or list your own goods — I take a 2% fee to post.", options: [
    { label: "Open the marketplace", action: "openMarket" },
    { label: "Leave" },
  ] },
};
/** The junk vendor — stands outside the warehouse; buys items for cash on the spot.
 *  (Renamed Sal → Gus when the QUEST-GIVER Sal arrived — one Sal per village.) */
const VENDOR: NpcDef = {
  name: "Gus", health: 110,
  appearance: A({ face: 0, skinTone: 4, hair: 4, hairColor: 0, top: 1, topColor: 3, bottom: 2, bottomColor: 0, shoes: 1, shoesColor: 4, hat: 1, hatColor: 1 }),
  lines: ["Got junk? I buy junk."],
  dialog: { text: "Got loot to offload? Drag it onto my counter and I'll pay you cash on the spot.", options: [
    { label: "Sell items", action: "openSellShop" },
    { label: "Leave" },
  ] },
};

export function villageNpcs(
  scene: THREE.Scene,
  spots: Record<"banker" | "casino" | "guardE" | "guardW" | "winston" | "miner" | "gardener" | "blackmarket" | "marketeer" | "vendor" | "fishmonger", { x: number; z: number; face: number }>,
  area?: { x0: number; z0: number; x1: number; z1: number },
): NpcManager {
  const spawns: Spawn[] = [
    { def: GUARD_E, kind: "face", x: spots.guardE.x, z: spots.guardE.z, face: spots.guardE.face },
    { def: GUARD_W, kind: "face", x: spots.guardW.x, z: spots.guardW.z, face: spots.guardW.face },
    { def: MINER, kind: "face", x: spots.miner.x, z: spots.miner.z, face: spots.miner.face },
    { def: GARDENER, kind: "face", x: spots.gardener.x, z: spots.gardener.z, face: spots.gardener.face },
    { def: BLACKMARKET, kind: "face", x: spots.blackmarket.x, z: spots.blackmarket.z, face: spots.blackmarket.face },
    { def: MARKETEER, kind: "face", x: spots.marketeer.x, z: spots.marketeer.z, face: spots.marketeer.face },
    { def: VENDOR, kind: "face", x: spots.vendor.x, z: spots.vendor.z, face: spots.vendor.face },
    { def: FISHMONGER, kind: "face", x: spots.fishmonger.x, z: spots.fishmonger.z, face: spots.fishmonger.face },
  ];
  // Sal, the first quest giver, paces the E–W main road (the manager's wander area).
  if (area) spawns.push({ def: SAL, kind: "wander", x: (area.x0 + area.x1) / 2, z: (area.z0 + area.z1) / 2 });
  // Ambient townsfolk are now SERVER-SYNCED (CityNpc crowds), so they aren't spawned client-side here.
  return new NpcManager(scene, spawns, area);
}

/** A few ambient villagers who wander the city streets (reusing the adventurer looks). */
const TOWNSFOLK: NpcDef[] = [
  { name: "Pip", health: 90, lines: ADVENTURER_LINES, appearance: A({ face: 0, skinTone: 2, hair: 1, hairColor: 4, top: 1, topColor: 6, bottom: 1, bottomColor: 2, shoes: 1, shoesColor: 1 }) },
  { name: "Dot", health: 90, lines: ADVENTURER_LINES, appearance: A({ face: 1, skinTone: 3, hair: 2, hairColor: 8, top: 3, topColor: 7, bottom: 2, bottomColor: 0, shoes: 1, shoesColor: 3 }) },
  { name: "Russ", health: 100, lines: ADVENTURER_LINES, appearance: A({ face: 0, skinTone: 4, hair: 4, hairColor: 0, top: 1, topColor: 5, bottom: 1, bottomColor: 1, shoes: 1, shoesColor: 0, hat: 1, hatColor: 5 }) },
];

/** Mr. Vault, behind the counter inside the BANK. */
export function bankNpcs(scene: THREE.Scene, spot: { x: number; z: number; face: number }): NpcManager {
  return new NpcManager(scene, [{ def: BANKER, kind: "face", x: spot.x, z: spot.z, face: spot.face }]);
}

/** The city WINSTON'S tavern: a few wandering adventurers + Winston behind the bar (he
 *  teleports you back to the spawn island). Built like the spawn-island tavern. */
export function cityTavernNpcs(
  scene: THREE.Scene,
  area: { x0: number; z0: number; x1: number; z1: number },
  barSpot: { x: number; z: number },
): NpcManager {
  const spawns: Spawn[] = ADVENTURER_ROSTER.slice(0, 3).map((def, i) => {
    const t = (i + 0.5) / 3;
    return { def, kind: "wander", x: area.x0 + t * (area.x1 - area.x0), z: area.z0 + ((i % 2) ? 0.65 : 0.3) * (area.z1 - area.z0) };
  });
  spawns.push({ def: CITY_WINSTON, kind: "face", x: barSpot.x, z: barSpot.z, face: faceTo(barSpot, area) });
  // Coco the salon stylist stands off to the side of the room (clear of the bar), facing the centre.
  const salonSpot = { x: area.x0 + 0.5, z: area.z0 + 0.3 };
  spawns.push({ def: SALON, kind: "face", x: salonSpot.x, z: salonSpot.z, face: faceTo(salonSpot, area) });
  return new NpcManager(scene, spawns, area);
}

/** Vex, inside the THREADS store. */
export function threadsNpcs(scene: THREE.Scene, spot: { x: number; z: number; face: number }): NpcManager {
  return new NpcManager(scene, [{ def: CLOTHIER, kind: "face", x: spot.x, z: spot.z, face: spot.face }]);
}

/** Sal, the food vendor behind the FOOD STORE counter. */
export function foodNpcs(scene: THREE.Scene, spot: { x: number; z: number; face: number }): NpcManager {
  return new NpcManager(scene, [{ def: FOOD_VENDOR, kind: "face", x: spot.x, z: spot.z, face: spot.face }]);
}

/** Oke, the clerk behind the OKKA FURNITURE counter. */
export function furniNpcs(scene: THREE.Scene, spot: { x: number; z: number; face: number }): NpcManager {
  return new NpcManager(scene, [{ def: FURNI_CLERK, kind: "face", x: spot.x, z: spot.z, face: spot.face }]);
}

/** The Old Timer behind his counter inside OLD TIMER'S BAITS (the fishing-tackle shack). */
export function tackleNpcs(scene: THREE.Scene, spot: { x: number; z: number; face: number }): NpcManager {
  return new NpcManager(scene, [{ def: FISHER, kind: "face", x: spot.x, z: spot.z, face: spot.face }]);
}

/** Ember the BLACKSMITH — behind the counter of the smithy on the residence row: sells honest
 *  steel (sword + shield) and teaches the Black Smith profession for 20 copper ore. */
const SMITH: NpcDef = {
  name: "Ember", health: 120,
  appearance: A({ face: 0, skinTone: 4, hair: 2, hairColor: 1, top: 1, topColor: 0, bottom: 2, bottomColor: 0, shoes: 1, shoesColor: 0 }),
  lines: [
    "Steel doesn't lie.",
    "The furnace does the work — I just don't let it quit.",
  ],
  dialog: { text: "Fire's hot, steel's true. What'll it be?", options: [
    { label: "Browse the steel", action: "openSmithShop" },
    {
      label: "Teach me Black Smith",
      next: {
        text: "Bring me 20 copper ore and the forge trade is yours.",
        options: [
          { label: "Learn it (20 copper ore)", action: "learnBlackSmith" },
          { label: "Not yet" },
        ],
      },
    },
    { label: "Leave" },
  ] },
};

export function smithNpcs(scene: THREE.Scene, spot: { x: number; z: number; face: number }): NpcManager {
  return new NpcManager(scene, [{ def: SMITH, kind: "face", x: spot.x, z: spot.z, face: spot.face }]);
}

/** Roxy (host, by the door) + Ace (poker dealer, behind the poker felt) on the CASINO floor. */
export function casinoNpcs(
  scene: THREE.Scene,
  spot: { x: number; z: number; face: number },
  pokerSpot?: { x: number; z: number; face: number },
): NpcManager {
  const spawns = [{ def: CASINO_HOST, kind: "face" as const, x: spot.x, z: spot.z, face: spot.face }];
  if (pokerSpot) spawns.push({ def: POKER_DEALER, kind: "face" as const, x: pokerSpot.x, z: pokerSpot.z, face: pokerSpot.face });
  return new NpcManager(scene, spawns);
}

export type { Npc };
